import { FFT_SIZE, SPECTRUM_COLUMN_COUNT } from './constants'
import type { AudioBands, AudioRhythmSettings } from './types'

const LOW_MAX_HZ = 250
const MID_MAX_HZ = 2000

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v))
}

function sensitivityGain(percent: number): number {
  return Math.max(0, percent / 100)
}

/** 频段分类与列映射缓存：log10 与 Hz 判断每帧重复 1024 次，改为按 sampleRate 预计算一次。 */
let cachedRate = 0
let cachedCols: number[] = []
let cachedBands: (0 | 1 | 2)[] = [] // 0=低频, 1=中频, 2=高频

function getBinMapping(sampleRate: number, usableBins: number, columnCount: number) {
  if (cachedRate !== sampleRate || cachedCols.length !== usableBins) {
    const binHz = sampleRate / FFT_SIZE
    cachedCols = new Array<number>(usableBins)
    cachedBands = new Array<0 | 1 | 2>(usableBins)
    for (let i = 0; i < usableBins; i++) {
      const hz = i * binHz
      const t = Math.log10(1 + (hz / (sampleRate / 2)) * 9) / Math.log10(10)
      cachedCols[i] = Math.min(columnCount - 1, Math.floor(t * columnCount))
      cachedBands[i] = hz <= LOW_MAX_HZ ? 0 : hz <= MID_MAX_HZ ? 1 : 2
    }
    cachedRate = sampleRate
  }
  return { cols: cachedCols, bands: cachedBands }
}

/**
 * 将 AnalyserNode 频域数据转为低/中/高与列频谱。
 * 采样率默认 48k；按 Hz 切分频带。
 */
export function extractBands(
  frequencyData: Uint8Array,
  sampleRate: number,
  settings: AudioRhythmSettings,
  prevLow: number,
  timeSec: number,
  columnCount = SPECTRUM_COLUMN_COUNT,
): AudioBands {
  const binHz = sampleRate / FFT_SIZE
  let lowSum = 0
  let lowCount = 0
  let midSum = 0
  let midCount = 0
  let highSum = 0
  let highCount = 0

  const usableBins = Math.min(frequencyData.length, Math.floor(sampleRate / 2 / binHz))
  const columns = new Array<number>(columnCount).fill(0)
  const colCounts = new Array<number>(columnCount).fill(0)

  const { cols, bands } = getBinMapping(sampleRate, usableBins, columnCount)
  for (let i = 1; i < usableBins; i++) {
    const v = (frequencyData[i] ?? 0) / 255
    const band = bands[i]!
    if (band === 0) {
      lowSum += v
      lowCount++
    } else if (band === 1) {
      midSum += v
      midCount++
    } else {
      highSum += v
      highCount++
    }

    const col = cols[i]!
    columns[col] += v
    colCounts[col]++
  }

  const rawLow = lowCount ? lowSum / lowCount : 0
  const rawMid = midCount ? midSum / midCount : 0
  const rawHigh = highCount ? highSum / highCount : 0

  const low = clamp01(rawLow * sensitivityGain(settings.lowSensitivity))
  const mid = clamp01(rawMid * sensitivityGain(settings.midSensitivity))
  const high = clamp01(rawHigh * sensitivityGain(settings.highSensitivity))
  const overall = clamp01(low * 0.45 + mid * 0.35 + high * 0.2)

  for (let c = 0; c < columnCount; c++) {
    const avg = colCounts[c] ? columns[c]! / colCounts[c]! : 0
    // 列能量混合三频灵敏度：左侧偏低频，右侧偏高频
    const mix =
      avg *
      (sensitivityGain(settings.lowSensitivity) * (1 - c / columnCount) +
        sensitivityGain(settings.midSensitivity) * 0.5 +
        sensitivityGain(settings.highSensitivity) * (c / columnCount))
    columns[c] = clamp01(mix)
  }

  // 简单节拍：低频相对上一帧的上升沿
  const rise = Math.max(0, low - prevLow)
  const beat = clamp01(rise * 4 + low * 0.15)

  return { low, mid, high, overall, columns, beat, timeSec }
}
